import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Cube, 
  ChartLineUp, 
  Lightbulb,
  GraduationCap,
  ArrowRight,
  Scissors,
  Circle,
  SignOut,
  UserCircle,
} from '@phosphor-icons/react';

const mainCards = [
  {
    id: 'gestao',
    title: 'Gestão',
    subtitle: 'CORE',
    description: 'Organize a operação, controle a produção e acompanhe os números do salão em tempo real.',
    icon: Cube,
    path: '/gestao',
    enabled: true,
    featured: true
  },
  {
    id: 'comercial',
    title: 'Comercial',
    subtitle: 'CONNECT',
    description: 'Gerencie leads, campanhas, relacionamento e recorrência.',
    icon: ChartLineUp,
    path: '/comercial',
    enabled: false,
    featured: false
  },
  {
    id: 'inteligencia',
    title: 'Inteligência',
    subtitle: 'MACD IA',
    description: 'Acesse ferramentas para crescer com método e direção.',
    icon: Lightbulb,
    path: '/inteligencia',
    enabled: false,
    featured: false
  },
  {
    id: 'academy',
    title: 'Academy',
    subtitle: 'Aulas — em breve',
    description: 'Aprenda a usar o SalonUp com aulas curtas e objetivas. Conteúdos premium em breve.',
    icon: GraduationCap,
    path: '/academy',
    enabled: false,
    featured: false
  }
];

export const Home = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleCardClick = (card) => {
    if (card.enabled) {
      navigate(card.path);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F6F4]" data-testid="home-page">
      <div className="salonup-container">
        {/* Header */}
        <header className="salonup-header animate-fade-in">
          <div className="salonup-brand">
            <div className="salonup-logo-mark">
              <Scissors size={22} weight="bold" />
            </div>
            <span className="salonup-logo-text">
              Salon<span>Up</span>
            </span>
          </div>
          
          <div className="flex items-center gap-4">
            {user && (
              <div className="flex items-center gap-3" data-testid="user-info">
                <div className="flex items-center gap-2 text-[#71717A]">
                  <UserCircle size={18} weight="regular" />
                  <span className="text-xs font-medium">{user.name}</span>
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#F0FDF4] text-[#1B4332] font-semibold uppercase">{user.role}</span>
                </div>
                <button onClick={logout} data-testid="btn-logout"
                  className="flex items-center gap-1 text-xs text-[#A1A1AA] hover:text-[#DC2626] transition-colors">
                  <SignOut size={14} weight="bold" />Sair
                </button>
              </div>
            )}
            <div className="salonup-version">
              <Circle size={6} weight="fill" className="text-green-500" />
              <span>v1.0</span>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="mb-10 md:mb-14 animate-fade-in-up opacity-0 delay-100" style={{ animationFillMode: 'forwards' }}>
          <div className="max-w-2xl">
            <h1 className="salonup-heading-xl mb-4">
              Bem-vindo ao <span className="text-[#1B4332]">SalonUp</span>
            </h1>
            <p className="salonup-text-body text-lg">
              O sistema operacional do salão moderno. Escolha a área que deseja acessar.
            </p>
          </div>
        </section>

        {/* Main Cards Grid */}
        <section className="salonup-grid-main">
          {mainCards.map((card, index) => {
            const Icon = card.icon;
            const isDisabled = !card.enabled;
            const isFeatured = card.featured;
            
            return (
              <div
                key={card.id}
                data-testid={`card-${card.id}`}
                onClick={() => handleCardClick(card)}
                className={`
                  salonup-card p-5 md:p-6
                  ${isFeatured ? 'salonup-card-featured' : ''}
                  ${isDisabled ? 'salonup-card-disabled' : ''}
                  animate-fade-in-up opacity-0
                `}
                style={{ animationDelay: `${150 + (index * 80)}ms`, animationFillMode: 'forwards' }}
              >
                <div className="flex flex-col h-full relative z-10">
                  {/* Badge row */}
                  <div className="flex items-center justify-between mb-5 min-h-[22px]">
                    {card.subtitle ? (
                      <span className="salonup-badge text-[9px]">
                        {card.subtitle}
                      </span>
                    ) : <span />}
                    {isFeatured && (
                      <div className="flex items-center gap-1.5">
                        <div className="salonup-status-dot" />
                        <span className="text-[9px] font-medium text-white/60 uppercase tracking-wider">Ativo</span>
                      </div>
                    )}
                  </div>

                  {/* Icon centered */}
                  <div className="flex justify-center mb-5">
                    <div className={`salonup-icon-wrapper ${isFeatured ? '' : 'salonup-icon-wrapper-sm'}`}>
                      <Icon size={isFeatured ? 26 : 22} weight="regular" />
                    </div>
                  </div>
                  
                  {/* Title centered */}
                  <h2 className={`salonup-heading-md text-center mb-2.5 leading-tight min-h-[1.75rem] ${isFeatured ? 'text-white' : ''}`}>
                    {card.title}
                  </h2>
                  
                  {/* Description centered */}
                  <p className={`salonup-text-sm text-center mb-5 leading-relaxed min-h-[3.9375rem] flex-1 ${isFeatured ? 'text-white/75' : ''}`}>
                    {card.description}
                  </p>
                  
                  {/* Action row */}
                  <div className="flex items-center justify-center min-h-[26px]">
                    {isDisabled ? (
                      <span className="salonup-badge-outline text-[10px]">Em breve</span>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className={`salonup-label text-[10px] ${isFeatured ? 'text-white/60' : ''}`}>
                          Acessar módulo
                        </span>
                        <ArrowRight 
                          size={14} 
                          weight="bold" 
                          className="salonup-arrow" 
                        />
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Featured card decorative elements */}
                {isFeatured && (
                  <>
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/4" />
                    <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/4" />
                  </>
                )}
              </div>
            );
          })}
        </section>

        {/* Footer */}
        <footer className="salonup-footer animate-fade-in opacity-0" style={{ animationDelay: '600ms', animationFillMode: 'forwards' }}>
          <p className="salonup-text-xs">
            SalonUp — O sistema operacional do salão moderno
          </p>
          <p className="salonup-text-xs">
            © 2026 SalonUp. Todos os direitos reservados.
          </p>
        </footer>
      </div>
    </div>
  );
};

export default Home;
